/* eslint-disable max-len */
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { filter, map } from 'rxjs/operators';
import { AppState } from 'src/app/store/app.reducer';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { Observable, Subscription } from 'rxjs';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { StandardNewValidator, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { CAWeekDay } from 'src/app/model/control-access';
import { selectMtmaGroupModify, selectMtmaSpecialPlanningsAll, selectMtmaWeeklySchedulesAll, } from 'src/app/store/device/device.selector';
import { MtmaRule, MtmaTimeslice } from 'src/app/model/mtma-rules-access';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { FlagTimeslice } from 'src/app/conf/flag-timeslices';

@Component({
	selector: 'app-mtma-control-access-group-easy-dialog',
	templateUrl: './mtma-control-access-group-easy-dialog.component.html',
})

export class MtmaControlAccessGroupEasyDialogDialogComponent implements OnInit, OnDestroy {

	groupsModify$: Observable<boolean | undefined>;
	groupsModifySub: Subscription | undefined;
	weeklySchedulesAll$: Observable<MtmaTimeslice[] | undefined>;
	weeklySchedulesAllSub: Subscription | undefined;
	specialPlanningsAll$: Observable<MtmaTimeslice[] | undefined>;
	specialPlanningsAllSub: Subscription | undefined;

	group: MtmaRule;
	editMode = false;

	groupForm: FormGroup;

	editedSub: Subscription;
	addedSub: Subscription;
	groupModifySub: Subscription;

	listWeeklySchedule: MtmaTimeslice[];
	listWeeklyScheduleSub: Subscription;
	weeklyScheduleCtrl = new FormControl();
	filteredWeeklySchedule: Observable<MtmaTimeslice[]>;

	listSpecialPlanning: MtmaTimeslice[];
	listSpecialPlanningSub: Subscription;
	specialPlanningCtrl = new FormControl();
	filteredSpecialPlanning: Observable<MtmaTimeslice[]>;

	// design
	screenWidth: number;

	constructor(
		private fb: FormBuilder,
		private store: Store<AppState>,
		private ref: DynamicDialogRef,
		public config: DynamicDialogConfig,
		private translate: TranslateService,
	) {
		this.group = new MtmaRule();
		if (config.data !== undefined && config.data.group !== undefined) {
			this.group = config.data.group;
			this.correctSpecialPlannings(this.group.SpecialPlannings);
			this.correctWeeklySchedule(this.group.WeeklySchedules);
			this.editMode = true;
		} else {
			this.group.WeeklySchedules = [];
			this.group.SpecialPlannings = [];
		}
		this.initSelectors();
	}

	initSelectors(): void {
		this.groupsModify$ = this.store.select(selectMtmaGroupModify);
		this.weeklySchedulesAll$ = this.store.select(selectMtmaWeeklySchedulesAll);
		this.specialPlanningsAll$ = this.store.select(selectMtmaSpecialPlanningsAll);
	}

	ngOnInit(): void {
		this.groupForm = this.fb.group({
			'Name': new FormControl(this.group.Name, [Validators.required, Validators.maxLength(48)]),
			'WeeklySchedule': new FormControl(this.group.WeeklySchedules),
			'SpecialPlanning': new FormControl(this.group.SpecialPlannings),
		});

		this.store.dispatch(DeviceActions.getMtmaWeeklySchedulesAll());
		this.store.dispatch(DeviceActions.getMtmaSpecialPlanningsAll());

		this.subscribeAll();

		this.onResize();
		StandardNewValidator.setFirstCompile(true);
	}

	subscribeAll(): void {
		this.groupsModifySub = this.groupsModify$
			.pipe(filter(groupsModify => !!groupsModify))
			.subscribe(() => {
				this.resetClose();
			});

		this.weeklySchedulesAllSub = this.weeklySchedulesAll$.subscribe(listWeeklySchedule => {
			this.listWeeklySchedule = listWeeklySchedule;
			this.correctWeeklySchedule(listWeeklySchedule);
		});
		this.specialPlanningsAllSub = this.specialPlanningsAll$.subscribe(listSpecialPlanning => {
			this.listSpecialPlanning = listSpecialPlanning;
			this.correctSpecialPlannings(listSpecialPlanning);
		});

		this.filteredWeeklySchedule = this.weeklyScheduleCtrl.valueChanges.pipe(
			map((weeklySchedule: string | null) => weeklySchedule ? this.filterWeeklySchedule(weeklySchedule) : this.listWeeklySchedule.slice())
		);
		this.filteredSpecialPlanning = this.specialPlanningCtrl.valueChanges.pipe(
			map((specialPlanning: string | null) => specialPlanning ? this.filterSpecialPlanning(specialPlanning) : this.listSpecialPlanning.slice())
		);
	}

	ngOnDestroy(): void {
		this.groupsModifySub?.unsubscribe();
		this.weeklySchedulesAllSub?.unsubscribe();
		this.specialPlanningsAllSub?.unsubscribe();
	}

	resetClose(): void {
		this.store.dispatch(DeviceActions.resetMtmaGroupForm());
		this.closeDialog();
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.groupForm.controls).forEach(key => {
			if (key !== 'WeeklySchedule' && key !== 'SpecialPlanning') {
				const controlErrors: ValidationErrors = this.groupForm.get(key).errors;
				if (controlErrors != null) {
					/* Object.keys(controlErrors).forEach(keyError => {
						console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
					}); */
					atLeastOneError = true;
				}
			}
		});
		const wsSpErr = this.showDoubleErrorRequiredMessageAndCheck(this.groupForm.value.WeeklySchedule,this.groupForm.value.SpecialPlanning)
		atLeastOneError = atLeastOneError || wsSpErr;
		return atLeastOneError;
	}

	save(): void {
		const group = new MtmaRule();
		group.Name = this.f.Name.value;
		group.WeeklySchedules = [];
		group.SpecialPlannings = [];

		if (this.f.WeeklySchedule.value !== null && this.f.WeeklySchedule.value !== undefined) {
			group.WeeklySchedules = this.f.WeeklySchedule.value;
		}
		if (this.f.SpecialPlanning.value !== null && this.f.SpecialPlanning.value !== undefined) {
			group.SpecialPlannings = this.f.SpecialPlanning.value;
		}
		if (this.group.Id) {
			group.Id = this.group.Id;
			this.store.dispatch(DeviceActions.editMtmaGroup({ group }));
		} else {
			this.store.dispatch(DeviceActions.addMtmaGroup({ group }));
		}
	}

	filterWeeklySchedule(value: string): MtmaTimeslice[] {
		if (typeof value === 'string') {
			const filterValue = value.toLowerCase();
			return this.listWeeklySchedule.filter(weeklySchedule => weeklySchedule.Name.toLowerCase().indexOf(filterValue) >= 0);
		}
		return this.listWeeklySchedule;
	}

	filterSpecialPlanning(value: string): MtmaTimeslice[] {
		if (typeof value === 'string') {
			const filterValue = value.toLowerCase();
			return this.listSpecialPlanning.filter(specialPlanning => specialPlanning.Name.toLowerCase().indexOf(filterValue) >= 0);
		}
		return this.listSpecialPlanning;
	}

	removeWeeklySchedule(weeklySchedule: MtmaTimeslice): void {
		const index = this.group.WeeklySchedules.indexOf(weeklySchedule);

		if (index >= 0) {
			this.group.WeeklySchedules.splice(index, 1);
		}
	}

	removeSpecialPlanning(specialPlanning: MtmaTimeslice): void {
		const index = this.group.SpecialPlannings.indexOf(specialPlanning);

		if (index >= 0) {
			this.group.SpecialPlannings.splice(index, 1);
		}
	}

	daysOfWeekSchedule(weeklySchedule: MtmaTimeslice): CAWeekDay[] {
		const currentWeekDays = [
			{ Value: '0', Name: 'SUN', Enable: false },
			{ Value: '1', Name: 'MON', Enable: false },
			{ Value: '2', Name: 'TUE', Enable: false },
			{ Value: '3', Name: 'WED', Enable: false },
			{ Value: '4', Name: 'THU', Enable: false },
			{ Value: '5', Name: 'FRI', Enable: false },
			{ Value: '6', Name: 'SAT', Enable: false },
		];
		const ppp = [];
		let value = weeklySchedule.Weekdays;
		for (let i = 6; i > -1; i--) {
			if (value !== 0 && value / (2 ** i) >= 1) {
				ppp.unshift(i.toString());
				currentWeekDays[i].Enable = true;
			}
			value = value % (2 ** i);
		}
		return currentWeekDays;
	}

	get f(): any {
		return this.groupForm.controls;
	}

	closeDialog(): void {
		this.ref.close();
	}

	isAccessSpecialPlanning(specialPlanning: MtmaTimeslice): boolean {
		if ((specialPlanning.Flags & (1 << 0)) > 0) {
			return true;
		}
		return false;
	}

	isOldSpecialPlanning(specialPlanning: MtmaTimeslice): boolean {
		if ((specialPlanning.Flags & (1 << 1)) > 0) { // bit1
			return true;
		}
		if ((specialPlanning.Flags & (1 << 2)) > 0) { // bit2
			return true;
		}
		if ((specialPlanning.Flags & (1 << 3)) > 0) { // bit3
			return true;
		}
		if ((specialPlanning.Flags & (1 << 4)) > 0) { // bit4
			return true;
		}
		if ((specialPlanning.Flags & (1 << 5)) > 0) { // bit5
			return true;
		}
		if ((specialPlanning.Flags & (1 << 6)) > 0) { // bit6
			return true;
		}
		return false;
	}

	isAccessWeeklySchedule(weeklySchedule: MtmaTimeslice): boolean {
		if ((weeklySchedule.Flags & (1 << 0)) > 0) {
			return true;
		}
		return false;
	}

	isOldWeeklySchedule(specialPlanning: MtmaTimeslice): boolean {
		if ((specialPlanning.Flags & (1 << 0)) === 0) { // bit0
			return true;
		}
		if ((specialPlanning.Flags & (1 << 1)) > 0) { // bit1
			return true;
		}
		if ((specialPlanning.Flags & (1 << 2)) > 0) { // bit2
			return true;
		}
		if ((specialPlanning.Flags & (1 << 3)) > 0) { // bit3
			return true;
		}
		if ((specialPlanning.Flags & (1 << 4)) > 0) { // bit4
			return true;
		}
		if ((specialPlanning.Flags & (1 << 5)) > 0) { // bit5
			return true;
		}
		if ((specialPlanning.Flags & (1 << 6)) > 0) { // bit6
			return true;
		}
		return false;
	}

	getTime(dtFrom: string): string {
		return dtFrom.split('T')[1];
	}

	//---------------------OLD VERSIONE

	getRepetitionTimeslice(timeslice: MtmaTimeslice): number {
		const listFlag = [];
		let value = timeslice.Flags;
		for (let i = 3; i > -1; i--) {
			if (value !== 0 && value / (2 ** i) >= 1) {
				listFlag.push(1);
			} else {
				listFlag.push(0);
			}
			value = value % (2 ** i);
		}
		if (listFlag[3 - FlagTimeslice.REPETITION_MONTHLY] === 1) {
			return FlagTimeslice.REPETITION_MONTHLY;
		} else if (listFlag[3 - FlagTimeslice.REPETITION_ANNUAL] === 1) {
			return FlagTimeslice.REPETITION_ANNUAL;
		}
		return 0;
	}

	getLabelRepetition(repetition: number): string {
		switch (repetition) {
			case 2: {   // MONTLY
				return this.translate.instant('MONTHLY');
			}
			case 3: {   // ANNUAL
				return this.translate.instant('REPETITION_ANNUAL');
			}
			default: {
				return "-";
			}
		}
	}

	getColorRepetition(repetition: number): string {
		switch (repetition) {
			case 2: {   // MONTLY
				return 'w-10rem p-tag-lightBlue0220';
			}
			case 3: {   // ANNUAL
				return 'w-10rem p-tag-lightBlue0240';
			}
			default: {
				return 'w-10rem p-tag-darkBlue10';
			}
		}
	}

	getDetailsTimeSpecialPlanning(timeslice: MtmaTimeslice): string {
		let date1 = '';
		let date2 = '';
		let time1 = '';
		let time2 = '';
		const momFrom = moment.utc(timeslice.DtFrom);
		const momTo = moment.utc(timeslice.DtTo);
		const repetition = this.getRepetitionTimeslice(timeslice);
		if (repetition === FlagTimeslice.REPETITION_MONTHLY) {
			const listFlag = [];
			let value = timeslice.Flags;
			for (let i = 6; i > -1; i--) {
				if (value !== 0 && value / (2 ** i) >= 1) {
					listFlag.push(1);
				} else {
					listFlag.push(0);
				}
				value = value % (2 ** i);
			}
			let dayMonthStart = momFrom.utc().date();
			let dayMonthEnd = momTo.utc().date();
			if (listFlag[6 - FlagTimeslice.DATE_29] + listFlag[6 - FlagTimeslice.DATE_30] + listFlag[6 - FlagTimeslice.DATE_31] > 0) {
				if (listFlag[6 - FlagTimeslice.DATE_31] === 1) {
					dayMonthEnd = 31;
				}
				if (listFlag[6 - FlagTimeslice.DATE_30] === 1 && listFlag[6 - FlagTimeslice.DATE_31] === 1) {
					dayMonthStart = 30;
				} else if (listFlag[6 - FlagTimeslice.DATE_30] === 1 && listFlag[6 - FlagTimeslice.DATE_31] === 0) {
					dayMonthEnd = 30;
				}
				if (listFlag[6 - FlagTimeslice.DATE_29] === 1 && (listFlag[6 - FlagTimeslice.DATE_31] === 1 || listFlag[6 - FlagTimeslice.DATE_30] === 1)) {
					dayMonthStart = 29;
				} else if (listFlag[6 - FlagTimeslice.DATE_29] === 1 && listFlag[6 - FlagTimeslice.DATE_31] === 0 && listFlag[6 - FlagTimeslice.DATE_30] === 0) {
					dayMonthEnd = 29;
				}
			}
			date1 = this.translate.instant('DAY_FROM') + ' ' + dayMonthStart;
			date2 = this.translate.instant('DAY_TO') + ' ' + dayMonthEnd;
			/*detail = detail + ' ' + this.translate.instant('DAY_FROM') + ' ' + dayMonthStart +
				' - ' + this.translate.instant('DAY_TO') + ' ' + dayMonthEnd;*/
			time1 = this.translate.instant('HOURS') + ' ' + momFrom.utc().format('HH:mm');
			time2 = this.translate.instant('HOURS') + ' ' + momTo.utc().format('HH:mm');
			/*detail = detail + ' ' + this.translate.instant('DATE_FROM') + ' ' + momFrom.format('HH:mm') +
			' - ' + this.translate.instant('DATE_TO') + ' ' + momTo.format('HH:mm');*/
		} else if (repetition === FlagTimeslice.REPETITION_ANNUAL) {
			date1 = this.translate.instant('DAY_MONTH_FROM') + ' ' + momFrom.utc().format('DD/MM');
			date2 = this.translate.instant('DAY_MONTH_TO') + ' ' + momTo.utc().format('DD/MM');
			/*detail = detail + ' ' + this.translate.instant('DAY_MONTH_FROM') + ' ' + momFrom.utc().format('DD/MM') +
				' - ' + this.translate.instant('DAY_MONTH_TO') + ' ' + momTo.utc().format('DD/MM');*/
			time1 = this.translate.instant('HOURS') + ' ' + momFrom.utc().format('HH:mm');
			time2 = this.translate.instant('HOURS') + ' ' + momTo.utc().format('HH:mm');
			/*detail = detail + ' ' + this.translate.instant('DATE_FROM') + ' ' + momFrom.format('HH:mm') +
			' - ' + this.translate.instant('DATE_TO') + ' ' + momTo.format('HH:mm');*/
		}
		return date1 + ' ' + time1 + ' - ' + date2 + ' ' + time2;
	}

	getDetailsTimeslice(timeslice: MtmaTimeslice): string {
		let detail = '';
		const momFrom = moment(timeslice.DtFrom);
		const momTo = moment(timeslice.DtTo);
		if (timeslice.Type === 2) {
			const repetition = this.getRepetitionTimeslice(timeslice);
			if (repetition === FlagTimeslice.REPETITION_MONTHLY) {
				const listFlag = [];
				let value = timeslice.Flags;
				for (let i = 6; i > -1; i--) {
					if (value !== 0 && value / (2 ** i) >= 1) {
						listFlag.push(1);
					} else {
						listFlag.push(0);
					}
					value = value % (2 ** i);
				}
				let dayMonthStart = momFrom.utc().date();
				let dayMonthEnd = momTo.utc().date();
				if (listFlag[6 - FlagTimeslice.DATE_29] + listFlag[6 - FlagTimeslice.DATE_30] + listFlag[6 - FlagTimeslice.DATE_31] > 0) {
					if (listFlag[6 - FlagTimeslice.DATE_31] === 1) {
						dayMonthEnd = 31;
					}
					if (listFlag[6 - FlagTimeslice.DATE_30] === 1 && listFlag[6 - FlagTimeslice.DATE_31] === 1) {
						dayMonthStart = 30;
					} else if (listFlag[6 - FlagTimeslice.DATE_30] === 1 && listFlag[6 - FlagTimeslice.DATE_31] === 0) {
						dayMonthEnd = 30;
					}
					if (listFlag[6 - FlagTimeslice.DATE_29] === 1 && (listFlag[6 - FlagTimeslice.DATE_31] === 1 || listFlag[6 - FlagTimeslice.DATE_30] === 1)) {
						dayMonthStart = 29;
					} else if (listFlag[6 - FlagTimeslice.DATE_29] === 1 && listFlag[6 - FlagTimeslice.DATE_31] === 0 && listFlag[6 - FlagTimeslice.DATE_30] === 0) {
						dayMonthEnd = 29;
					}
				}
				detail = detail + ' ' + this.translate.instant('DAY_FROM') + ' ' + dayMonthStart +
					' - ' + this.translate.instant('DAY_TO') + ' ' + dayMonthEnd;
			} else if (repetition === FlagTimeslice.REPETITION_ANNUAL) {
				detail = detail + ' ' + this.translate.instant('DAY_MONTH_FROM') + ' ' + momFrom.utc().format('DD/MM') +
					' - ' + this.translate.instant('DAY_MONTH_TO') + ' ' + momTo.utc().format('DD/MM');
			}

		}
		return detail;
	}

	getTimeTimeslice(timeslice: MtmaTimeslice): string {
		const momFrom = moment(timeslice.DtFrom);
		const momTo = moment(timeslice.DtTo);

		let detail = '';
		detail = detail + ' ' + this.translate.instant('DATE_FROM') + ' ' + momFrom.format('HH:mm') +
			' - ' + this.translate.instant('DATE_TO') + ' ' + momTo.format('HH:mm');

		return detail;
	}

	getTemporaryTimeslice(timeslice: MtmaTimeslice): string {
		const listFlag = [];
		const momFrom = moment(timeslice.DtFrom);
		const momTo = moment(timeslice.DtTo);
		let value = timeslice.Flags;
		for (let i = 6; i > -1; i--) {
			if (value !== 0 && value / (2 ** i) >= 1) {
				listFlag.push(1);
			} else {
				listFlag.push(0);
			}
			value = value % (2 ** i);
		}
		let detail = '-';
		if (listFlag[6 - FlagTimeslice.TEMPORARY] === 1) {
			if (timeslice.Type === 1) {
				detail = this.translate.instant('FROM') + ' ' + momFrom.format('DD/MM/yyyy') +
					' - ' + this.translate.instant('TO') + ' ' + momTo.format('DD/MM/yyyy');
			} else if (timeslice.Type === 2) {
				const repetition = this.getRepetitionTimeslice(timeslice);
				if (repetition === FlagTimeslice.REPETITION_MONTHLY) {
					detail = this.translate.instant('DAY_MONTH_FROM') + ' ' + momFrom.format('MM/yyyy') +
						' - ' + this.translate.instant('DAY_MONTH_TO') + ' ' + momTo.format('MM/yyyy');
				} else if (repetition === FlagTimeslice.REPETITION_ANNUAL) {
					detail = this.translate.instant('FROM') + ' ' + momFrom.format('yyyy') +
						' - ' + this.translate.instant('TO') + ' ' + momTo.format('yyyy');
				}

			}
		}
		return detail;
	}

	//---------------------OLD VERSIONE

	correctSpecialPlannings(specialPlannings: MtmaTimeslice[]): void {
		if (specialPlannings != undefined && specialPlannings.length > 0) {
			for (let i = 0; i < specialPlannings.length; i++) {
				specialPlannings[i].Access = this.isAccessSpecialPlanning(specialPlannings[i]);
				specialPlannings[i].Old = this.isOldSpecialPlanning(specialPlannings[i]);
			}
		}
	}

	correctWeeklySchedule(weeklySchedules: MtmaTimeslice[]): void {
		if (weeklySchedules != undefined && weeklySchedules.length > 0) {
			for (let i = 0; i < weeklySchedules.length; i++) {
				weeklySchedules[i].Access = this.isAccessWeeklySchedule(weeklySchedules[i]);
				weeklySchedules[i].Old = this.isOldWeeklySchedule(weeklySchedules[i]);
			}
		}
	}
	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-23rem');
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	newShowErrors(type: MtmaControlAccessGroupEasyDialogDialogComponent["f"], classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	showErrorRequiredMessage(type: MtmaControlAccessGroupEasyDialogDialogComponent["f"]): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	showDoubleErrorRequiredMessageAndCheck(typeWs: MtmaControlAccessGroupEasyDialogDialogComponent["f"], typeSp: MtmaControlAccessGroupEasyDialogDialogComponent["f"]): boolean {
		return StandardNewValidator.showDoubleErrorRequiredMessageAndCheck(typeWs,typeSp);
	}

	simpleInputTextLayout(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}

	customPaddingX(): string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}
}
